import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { Button, Input, SVGIcon } from '@pearsontechnology/aero-components';

import { ModalContent, ModalActions } from 'components/Modal';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { getIcon, getColor } from 'Workspace/helpers';
import { selectWorkspacesList } from 'Workspace/Admin/store/selectors';
import pluginTypes from 'constants/pluginTypes';
import enhanceWithServices from 'hocs/enhanceWithServices';
import workspaceService from 'services/WorkspaceService';
import messages from './messages';
import { STEPS } from './constants';
import { ListWrapper, ListItem, IconBack, BackButton, Label, ModalTitle } from './styles';

function CoursewareSelector({
  selector,
  lessonId,
  onBack,
  onNext,
  primaryHandler,
  onDismiss,
  projectService,
  activityService,
  theme,
  enableUnitSelection,
  workspaces,
  primaryButtonText,
  selectWorkspaceOnly,
  showSelectedWorkspace,
}) {
  const workspaceId = workspaceService.getSelectedWorkspaceId();
  const [coursewareList, setCoursewareList] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isPrimaryButtonDisable, setIsPrimaryButtonDisable] = useState(true);
  const [selectedCoursewareItem, setSelectedCoursewareItem] = useState({});
  useEffect(() => {
    const getActivity = async () => {
      let coursewareItems = [];

      switch (selector.type) {
        case STEPS.MAIN:
          coursewareItems = workspaces;
          break;
        case STEPS.WORKSPACE:
          coursewareItems = await projectService.getProjectsList(showSelectedWorkspace ? workspaceId : selector.id);
          coursewareItems.sort(sortCoursewareByField('name'));
          break;
        case STEPS.PROJECT:
          coursewareItems = await projectService.getProjectActivityList(
            selector.id,
            ['title', 'author']
          );
          coursewareItems.sort(sortCoursewareByField('configFields.title'));
          break;
        default:
          coursewareItems = await activityService.getChildActivities(
            selector.id
          );
          coursewareItems.sort(sortCoursewareByField('name'));
          break;
      }

      setCoursewareList(coursewareItems);
      setIsDataLoaded(true);
    };
    getActivity();
  }, [
    projectService,
    lessonId,
    selector.id,
    selector.type,
    activityService,
    workspaces,
    showSelectedWorkspace,
    workspaceId,
  ]);

  const sortCoursewareByField = (field) => (a, b) => {
    const getValue = (obj, path) => path.split('.').reduce((o, k) => o?.[k], obj);
    const aValue = getValue(a, field)?.toLowerCase?.();
    const bValue = getValue(b, field)?.toLowerCase?.();
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  };

  const filterList = (value) => {
    if (!value) return coursewareList;

    const searchValue = value.toLowerCase();
    return coursewareList.filter((activity) => {
      let title;
      if (selector.type === STEPS.MAIN || selector.type === STEPS.WORKSPACE) {
        title = activity?.name;
      } else {
        title = activity.configFields?.title || activity.config?.title;
      }
      return title?.toLowerCase().includes(searchValue);
    });
  };

  const handleSearchInput = (event) => {
    setFilterValue(event.target.value);
  };

  const handleClickBack = () => {
    resetSearchAndSelectedItem();
    onBack();
    setIsPrimaryButtonDisable(true);
  };

  const resetSearchAndSelectedItem = () => {
    setFilterValue('');
    setSelectedCoursewareItem({});
  };

  const handlePrimaryAction = () => {
    primaryHandler(selectedCoursewareItem);
  };

  const selectListItem = (listItem) => async () => {
    if (selector.type === STEPS.MAIN) {
      // do not get activity detail if picking workspace
      if (selectWorkspaceOnly) {
        //do not go on next when component used in selectWorkspaceOnly
        setSelectedCoursewareItem({ ...listItem });
        setIsPrimaryButtonDisable(false);
        return;
      }
      handleOnNext(listItem.id, STEPS.WORKSPACE);
      return;
    }
    if (selector.type === STEPS.WORKSPACE) {
      // do not get activity detail if picking project
      handleOnNext(listItem.id, STEPS.PROJECT);
      return;
    }

    const activity = await activityService.getActivity(listItem.activityId);
    const { childrenPathways, plugin: { type } } = activity;
    const selectedItem = {
      ...listItem,
      parentPathwayId: childrenPathways
        ? childrenPathways[0]
        : listItem.activityId,
    };

    if ((enableUnitSelection && type === pluginTypes.UNIT) || (!enableUnitSelection && type === pluginTypes.COURSE) ||
      (type === pluginTypes.LESSON)) {
      setSelectedCoursewareItem(selectedItem);
      setIsPrimaryButtonDisable(false);
    } else {
      setSelectedCoursewareItem(selectedItem);
      handleOnNext(selectedItem.activityId, pluginTypes.COURSE);
    }
  };

  const handleOnNext = (listItemId, itemType) => {
    resetSearchAndSelectedItem();
    onNext({
      id: listItemId,
      type: itemType,
    });
  };

  const filteredCoursewareList = filterList(filterValue);

  const getModalTitle = () => intl.formatMessage(selector?.type === STEPS.WORKSPACE ? messages.selectProject : messages.publicationCourseware);

  const modalTitle = getModalTitle();

  return (
    <Fragment>
      <ModalContent unpadBottom>
        <ModalTitle>{modalTitle}</ModalTitle>
        <Input.Search
          width={'100%'}
          onChange={handleSearchInput}
          value={filterValue}
          placeholder={intl.formatMessage(messages.searchCourseware)}
          label={intl.formatMessage(messages.searchCourseware)}
        />
      </ModalContent>
      <ListWrapper data-testid='list-wrapper'>
        <BackButton
          disabled={selector.type === (showSelectedWorkspace ? STEPS.WORKSPACE : STEPS.MAIN)}
          onClick={handleClickBack}
        >
          <IconBack
            icon="back"
            disabled={selector.type === STEPS.MAIN}
            size="small"
          />
          <Label>{intl.formatMessage(messages.backButton)}</Label>
        </BackButton>
        {filteredCoursewareList.map((activity) => (
          <ListItem
            className="list-item"
            key={activity.activityId || activity.id}
            selected={
              selectedCoursewareItem.activityId ===
              (activity.activityId || activity.id)
            }
            onClick={selectListItem(activity)}
          >
            <SVGIcon
              icon={getIcon(activity?.plugin?.type || 'project')}
              viewBox="0 0 24 24"
              fill={getColor(activity?.plugin?.type || 'project', theme)}
              width={{ width: '24px', height: '24px' }}
            />
            {activity.name ||
              activity?.configFields?.title ||
              activity.config?.title}
          </ListItem>
        ))}
        {filteredCoursewareList.length === 0 && isDataLoaded
          ? intl.formatMessage(messages.emptyStateMessage)
          : ''}
      </ListWrapper>
      <ModalActions scrim>
        <Button grey onClick={onDismiss}>
          {intl.formatMessage(messages.cancelButton)}
        </Button>
        <Button primary disabled={isPrimaryButtonDisable} onClick={handlePrimaryAction}>
          {
            primaryButtonText ? primaryButtonText : intl.formatMessage(messages.selectButton)
          }
        </Button>
      </ModalActions>
    </Fragment>
  );
}

CoursewareSelector.propTypes = {
  selector: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    parent: PropTypes.object,
  }),
  lessonId: PropTypes.string,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  primaryHandler: PropTypes.func,
  onDismiss: PropTypes.func,
  projectService: PropTypes.object,
  theme: PropTypes.object,
  activityService: PropTypes.object,
  enableUnitSelection: PropTypes.bool,
  workspaces: PropTypes.array,
  selectWorkspaceOnly: PropTypes.bool,
  showSelectedWorkspace: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  workspaces: selectWorkspacesList(state),
});

export default compose(
  connect(mapStateToProps),
  withTheme,
  enhanceWithServices,
)(CoursewareSelector);
